import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { login } from "../../../store/authSlice";
import useToast from "../../../hooks/userToast";
import { useForm } from "react-hook-form";
import { logo } from "../../imagepath";
import loginBanner from "../../../assets/img/login-banner.png";

// ─── Neural-network canvas animation ──────────────────────────────────────────
function NeuralCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.parentElement.clientWidth;
      canvas.height = canvas.parentElement.clientHeight;
      init();
    };

    const init = () => {
      particles = [];
      const n = Math.floor((canvas.width * canvas.height) / 12000);
      for (let i = 0; i < n; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.6,
          vy: (Math.random() - 0.5) * 0.6,
          r: Math.random() * 1.5 + 0.5,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(99,102,241,0.8)";
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const d = Math.hypot(p.x - p2.x, p.y - p2.y);
          if (d < 130) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(99,102,241,${0.4 * (1 - d / 130)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };

    window.addEventListener("resize", resize);
    resize();
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        opacity: 0.6,
      }}
    />
  );
}

// ─── Main Login component ──────────────────────────────────────────────────────
const Login = ({ FCMToken }) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toastError, toastSuccess } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (data) => {
    const formData = { ...data, device_token: FCMToken };
    setLoading(true);
    dispatch(login(FCMToken ? formData : data))
      .unwrap()
      .then((res) => {
        localStorage.setItem("UserData", JSON.stringify(res));
        toastSuccess("Logged in Successfully");
        navigate("/studies");
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toastError("Invalid Email or Password");
        setLoading(false);
      });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

        .rdy-login-root {
          font-family: 'Inter', sans-serif;
          background-color: #060B14;
          color: #E2E8F0;
          height: 100vh;
          width: 100%;
          display: flex;
          overflow: hidden;
        }

        /* ── Dot grid ── */
        .rdy-dots {
          background-image: radial-gradient(rgba(99,102,241,0.15) 1px, transparent 1px);
          background-size: 24px 24px;
        }

        /* ── Floating showcase card ── */
        @keyframes rdy-hover-float {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-10px); }
        }
        .rdy-float { animation: rdy-hover-float 6s ease-in-out infinite; }

        /* ── Vertical scan line ── */
        @keyframes rdy-scan-line {
          0%   { top: 0%;   opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .rdy-scan-line {
          position: absolute;
          left: 0; right: 0;
          height: 2px;
          background: linear-gradient(to right, transparent, rgba(129,140,248,1), transparent);
          box-shadow: 0 0 20px rgba(129,140,248,1);
          z-index: 20;
          animation: rdy-scan-line 4s linear infinite;
        }

        /* ── Status dot pulse ── */
        @keyframes rdy-pulse {
          0%,100% { opacity: 1; }
          50%      { opacity: 0.4; }
        }
        .rdy-pulse { animation: rdy-pulse 1.5s ease-in-out infinite; }

        /* ── Input autofill fix ── */
        .rdy-input:-webkit-autofill,
        .rdy-input:-webkit-autofill:hover,
        .rdy-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 30px #0E1629 inset !important;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s;
        }

        /* ── Input focus ring ── */
        .rdy-input:focus {
          outline: none;
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 1px #3b82f6;
        }

        /* ── Submit button hover ── */
        .rdy-submit-btn:hover { background-color: #2563eb !important; }
        .rdy-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* ── Left panel responsive ── */
        @media (max-width: 1023px) {
          .rdy-left-panel { display: none !important; }
          .rdy-right-panel { width: 100% !important; }
        }
      `}</style>

      <div className="rdy-login-root">

        {/* ── LEFT: Product Showcase ── */}
        <div
          className="rdy-left-panel"
          style={{
            width: "55%",
            background: "linear-gradient(135deg, #1a0b2e 0%, #0A101E 100%)",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRight: "1px solid rgba(51,65,85,0.6)",
            overflow: "hidden",
          }}
        >
          <NeuralCanvas />

          {/* Dot grid */}
          <div
            className="rdy-dots"
            style={{ position: "absolute", inset: 0, opacity: 0.6, zIndex: 0 }}
          />

          {/* Ambient glows */}
          <div style={{
            position: "absolute", top: "25%", left: "25%",
            width: 384, height: 384,
            background: "rgba(99,102,241,0.2)",
            borderRadius: "50%", filter: "blur(100px)", zIndex: 0,
          }} />
          <div style={{
            position: "absolute", bottom: "25%", right: "25%",
            width: 384, height: 384,
            background: "rgba(168,85,247,0.1)",
            borderRadius: "50%", filter: "blur(100px)", zIndex: 0,
          }} />

          {/* Floating HUD frame */}
          <div
            className="rdy-float"
            style={{
              position: "relative", zIndex: 10,
              width: "min(500px, 85%)",
              aspectRatio: "4/3",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <div style={{
              position: "absolute", inset: 0,
              border: "1px solid rgba(99,102,241,0.3)",
              borderRadius: 16,
              background: "rgba(30,27,75,0.2)",
              backdropFilter: "blur(12px)",
              boxShadow: "0 0 50px rgba(79,70,229,0.15)",
              overflow: "hidden",
            }}>
              {/* Scan line */}
              <div className="rdy-scan-line" />

              {/* MRI / banner image */}
              <img
                src={loginBanner}
                alt="Medical Imaging"
                style={{
                  width: "100%", height: "100%",
                  objectFit: "cover",
                  opacity: 0.65,
                  mixBlendMode: "luminosity",
                  transform: "scale(1.05)",
                  transition: "transform 0.7s ease",
                }}
                onError={(e) => {
                  e.target.src =
                    "https://images.unsplash.com/photo-1516549655169-df83a0774514?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
                }}
              />

              {/* Grid overlay */}
              <div style={{
                position: "absolute", inset: 0, zIndex: 10,
                backgroundImage:
                  "linear-gradient(to right,rgba(129,140,248,0.08) 1px,transparent 1px),linear-gradient(to bottom,rgba(129,140,248,0.08) 1px,transparent 1px)",
                backgroundSize: "40px 40px",
              }} />

              {/* Corner accents */}
              {[
                { top: 20, left: 20, borderTop: "2px solid rgba(129,140,248,0.7)", borderLeft: "2px solid rgba(129,140,248,0.7)" },
                { top: 20, right: 20, borderTop: "2px solid rgba(129,140,248,0.7)", borderRight: "2px solid rgba(129,140,248,0.7)" },
                { bottom: 20, left: 20, borderBottom: "2px solid rgba(129,140,248,0.7)", borderLeft: "2px solid rgba(129,140,248,0.7)" },
                { bottom: 20, right: 20, borderBottom: "2px solid rgba(129,140,248,0.7)", borderRight: "2px solid rgba(129,140,248,0.7)" },
              ].map((s, i) => (
                <div key={i} style={{ position: "absolute", width: 28, height: 28, zIndex: 20, ...s }} />
              ))}

              {/* HUD data readout */}
              <div style={{
                position: "absolute", top: 24, left: 32, zIndex: 20,
                fontFamily: "monospace", fontSize: 10, color: "rgba(165,180,252,0.9)",
                lineHeight: 1.8,
              }}>
                <div><span style={{ color: "rgba(99,102,241,0.8)" }}>SYS:</span> ONLINE</div>
                <div><span style={{ color: "rgba(99,102,241,0.8)" }}>MOD:</span> 3D_RECON</div>
                <div><span style={{ color: "rgba(99,102,241,0.8)" }}>PWR:</span> 100%</div>
              </div>

              {/* Bottom status bar */}
              <div style={{
                position: "absolute", bottom: 20, left: 24, right: 24,
                display: "flex", justifyContent: "space-between", alignItems: "flex-end",
                zIndex: 20,
              }}>
                <div style={{
                  fontFamily: "monospace", fontSize: 10, letterSpacing: "0.15em",
                  color: "rgba(199,210,254,0.9)",
                  background: "rgba(30,27,75,0.6)",
                  padding: "6px 12px", borderRadius: 4,
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(99,102,241,0.2)",
                }}>
                  DIAGNOSTIC CORE{" "}
                  <span className="rdy-pulse" style={{ color: "rgba(129,140,248,1)" }}>_</span>
                </div>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "rgba(30,27,75,0.6)",
                  padding: "6px 12px", borderRadius: 4,
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(99,102,241,0.2)",
                }}>
                  <div className="rdy-pulse" style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#34d399",
                    boxShadow: "0 0 10px rgba(52,211,153,0.8)",
                  }} />
                  <span style={{
                    fontFamily: "monospace", fontSize: 9,
                    fontWeight: 700, letterSpacing: "0.1em", color: "#34d399",
                  }}>
                    SECURE CONNECTION
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div style={{
            marginTop: 40, display: "flex", gap: 28, zIndex: 10,
            fontFamily: "monospace", fontSize: 11,
            color: "rgba(165,180,252,0.6)", letterSpacing: "0.1em", textTransform: "uppercase",
          }}>
            {[
              { dot: "#10b981", shadow: "rgba(16,185,129,0.8)", label: "DICOM Ready" },
              { dot: "#818cf8", shadow: "rgba(129,140,248,0.8)", label: "PACS Connected" },
              { dot: "#38bdf8", shadow: "rgba(56,189,248,0.8)", label: "HIPAA Compliant" },
            ].map(({ dot, shadow, label }) => (
              <span key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: dot, boxShadow: `0 0 8px ${shadow}`,
                  display: "inline-block",
                }} />
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Login Form ── */}
        <div
          className="rdy-right-panel"
          style={{
            width: "45%",
            background: "#060B14",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Form area */}
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            justifyContent: "center",
            padding: "0 clamp(2rem, 8vw, 5rem)",
            position: "relative", zIndex: 10,
          }}>

            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 44 }}>
              <img
                src={logo}
                alt="Radiyantra"
                style={{ height: 36, width: "auto", objectFit: "contain" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>

            {/* Heading */}
            <div style={{ marginBottom: 36 }}>
              <h1 style={{
                fontSize: 28, fontWeight: 700, color: "#fff",
                margin: "0 0 10px 0", lineHeight: 1.2,
              }}>
                Sign in
              </h1>
              <p style={{ fontSize: 14, color: "#94a3b8", margin: 0, lineHeight: 1.6 }}>
                Enter your credentials to access the AI diagnostic suite and medical imaging platform.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: 18 }}>

              {/* Email */}
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#cbd5e1", marginBottom: 6 }}>
                  Email
                </label>
                <input
                  type="email"
                  className="rdy-input"
                  placeholder="doctor@hospital.com"
                  style={{
                    display: "block", width: "100%", boxSizing: "border-box",
                    padding: "12px 16px",
                    background: "#0E1629",
                    border: `1px solid ${errors.email ? "#ef4444" : "#1e293b"}`,
                    borderRadius: 8, color: "#fff",
                    fontSize: 14, transition: "border-color 0.2s, box-shadow 0.2s",
                  }}
                  {...register("email", { required: "Email is required" })}
                />
                {errors.email && (
                  <small style={{ color: "#f87171", fontSize: 12, marginTop: 4, display: "block" }}>
                    {errors.email.message}
                  </small>
                )}
              </div>

              {/* Password */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, color: "#cbd5e1" }}>
                    Password
                  </label>
                  <a
                    href="#"
                    style={{ fontSize: 12, fontWeight: 500, color: "#60a5fa", textDecoration: "none" }}
                    onMouseEnter={(e) => (e.target.style.color = "#93c5fd")}
                    onMouseLeave={(e) => (e.target.style.color = "#60a5fa")}
                  >
                    Forgot password?
                  </a>
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type={passwordVisible ? "text" : "password"}
                    className="rdy-input"
                    placeholder="Enter your password"
                    style={{
                      display: "block", width: "100%", boxSizing: "border-box",
                      padding: "12px 48px 12px 16px",
                      background: "#0E1629",
                      border: `1px solid ${errors.password ? "#ef4444" : "#1e293b"}`,
                      borderRadius: 8, color: "#fff",
                      fontSize: 14, transition: "border-color 0.2s, box-shadow 0.2s",
                    }}
                    {...register("password", { required: "Password is required" })}
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible((v) => !v)}
                    style={{
                      position: "absolute", top: 0, right: 0, bottom: 0,
                      padding: "0 14px",
                      background: "none", border: "none", cursor: "pointer",
                      color: "#64748b", display: "flex", alignItems: "center",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#cbd5e1")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#64748b")}
                  >
                    {passwordVisible ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <small style={{ color: "#f87171", fontSize: 12, marginTop: 4, display: "block" }}>
                    {errors.password.message}
                  </small>
                )}
              </div>

              {/* Remember me */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0 8px" }}>
                <input
                  id="remember"
                  type="checkbox"
                  style={{
                    width: 16, height: 16, borderRadius: 4,
                    accentColor: "#3b82f6", cursor: "pointer",
                  }}
                />
                <label htmlFor="remember" style={{ fontSize: 14, color: "#94a3b8", cursor: "pointer" }}>
                  Remember this device
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="rdy-submit-btn"
                style={{
                  width: "100%",
                  padding: "13px 16px",
                  background: "#2563eb",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  boxShadow: "0 0 20px rgba(59,130,246,0.2)",
                  transition: "background-color 0.2s",
                }}
              >
                {loading ? "Signing in…" : (
                  <>
                    Sign in
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Encrypted notice */}
            <div style={{ marginTop: 28, display: "flex", justifyContent: "center" }}>
              <p style={{ fontSize: 12, color: "#334155", display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                End-to-end encrypted connection
              </p>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: "16px 24px",
            borderTop: "1px solid rgba(30,41,59,0.5)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12,
            color: "#475569",
            background: "#060B14",
            zIndex: 10,
          }}>
            <a
              href="#"
              style={{
                color: "#475569", textDecoration: "none",
                display: "flex", alignItems: "center", gap: 6,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Support Team
            </a>
            <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.94-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                +91 7877066913
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(52,211,153,0.8)" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                24 × 7
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

Login.propTypes = {
  FCMToken: PropTypes.string,
  tokenError: PropTypes.string,
};

export default Login;
